import { useEffect, useState } from "react";

const BRANDFETCH_CLIENT_ID = "1idnOXCRSpyX9ESwWA3";

const normalizeDomain = (raw: string): string =>
  raw
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "");

const buildUrl = (
  domain: string,
  variant: "icon" | "logo",
  width: number,
  height: number
): string =>
  `https://cdn.brandfetch.io/${encodeURIComponent(domain)}` +
  `/w/${width}/h/${height}/theme/dark/fallback/lettermark/type/${variant}` +
  `?c=${BRANDFETCH_CLIENT_ID}`;

const initialsOf = (name?: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const letters =
    parts.length === 1
      ? parts[0].slice(0, 2)
      : parts[0][0] + parts[parts.length - 1][0];
  return letters.toUpperCase();
};

interface SntOrgBrandIconProps {
  domain?: string | null;
  name?: string;
  size?: number;
  variant?: "icon" | "logo";
  className?: string;
}

const SntOrgBrandIcon = ({
  domain,
  name,
  size = 32,
  variant = "icon",
  className = "",
}: SntOrgBrandIconProps) => {
  const [errored, setErrored] = useState(false);

  useEffect(() => setErrored(false), [domain, variant]);

  const cssWidth = variant === "logo" ? size * 4 : size;
  const cssHeight = size;

  const baseStyle = {
    width: cssWidth,
    height: cssHeight,
    background: "#ffffff",
    border: "1px solid #b8bfca",
    borderRadius: 6,
    boxSizing: "border-box" as const,
  };

  const cleaned = domain ? normalizeDomain(domain) : "";
  const hasDomain = !!cleaned && cleaned.includes(".");

  if (!hasDomain || errored) {
    return (
      <span
        className={`snt-org-icon-fallback ${className}`.trim()}
        style={{
          ...baseStyle,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#1d283c",
          fontWeight: 600,
          fontSize: Math.round(cssHeight * 0.4),
          lineHeight: 1,
          userSelect: "none",
        }}
        aria-label={name ? `${name} logo` : "Logo"}
      >
        {initialsOf(name)}
      </span>
    );
  }

  const pxWidth = Math.max(64, cssWidth * 2);
  const pxHeight = Math.max(64, cssHeight * 2);

  return (
    <img
      src={buildUrl(cleaned, variant, pxWidth, pxHeight)}
      alt={name ? `${name} logo` : "Logo by Brandfetch"}
      loading="lazy"
      onError={() => setErrored(true)}
      className={`${variant === "logo" ? "snt-org-logo" : "snt-org-icon"} ${className}`.trim()}
      style={{ ...baseStyle, objectFit: "contain" }}
    />
  );
};

export default SntOrgBrandIcon;
